import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import WheelChart from '../components/WheelChart';
import LifeSquares from '../components/LifeSquares';
import { wheelCategories, coachingQuestions } from '../lib/questions';
import { ArrowLeft, Download, CheckCircle, ExternalLink, Loader2, Mic } from 'lucide-react';
import { exportToPDF } from '../lib/pdfExport';
import { toast } from 'sonner';
import { configureSubmissionRmPassword, createAudioToken, finalizeSubmission } from '../lib/api';
import RmPasswordSetup from '../components/RmPasswordSetup';

export function StepSummary({
  userData,
  wheelData,
  lifeSquares,
  objectives,
  questionAnswers,
  sessionId,
  submissionId,
  isFormu2 = false,
  portalJourney = false,
  portalUrl = 'https://portal.academiacortex.com.ar/inicio',
  audioStatus = 'not_started',
  rmcPasswordConfigured = false,
  onRmcPasswordConfigured,
  onAudioTokenChange,
  onAudioUrlChange,
  onBack,
}) {
  const [isRecoveringAudio, setIsRecoveringAudio] = useState(false);
  const passwordSetupRef = useRef(null);
  const hasUploadedAudio = audioStatus === 'recording_uploaded' || audioStatus === 'submitted_to_editor';
  const canRecoverAudio = isFormu2
    && submissionId
    && !hasUploadedAudio;

  // Finalize submission when reaching summary
  useEffect(() => {
    if (submissionId) {
      finalizeSubmission(submissionId, sessionId).catch((error) => {
        console.error('Error finalizing submission on summary mount:', error);
      });
    }
  }, [submissionId, sessionId]);

  useEffect(() => {
    if (!isFormu2 || !hasUploadedAudio || rmcPasswordConfigured) return undefined;

    const focusTimer = window.setTimeout(() => {
      const passwordSetup = passwordSetupRef.current;
      if (!passwordSetup) return;

      passwordSetup.scrollIntoView({ behavior: 'smooth', block: 'start' });
      passwordSetup.focus({ preventScroll: true });
    }, 150);

    return () => window.clearTimeout(focusTimer);
  }, [hasUploadedAudio, isFormu2, rmcPasswordConfigured, submissionId]);

  const handleExportAllPDF = async () => {
    if (!submissionId) {
      toast.error('Este formulario todavía no está guardado en el servidor. Volvé al cierre y finalizalo antes de descargar.');
      return;
    }

    const success = await exportToPDF(
      'full-summary-content',
      `LifePlanner-${userData.name?.replace(/\s+/g, '_') || 'Usuario'}-${new Date().toISOString().split('T')[0]}.pdf`
    );
    if (success) {
      toast.success('¡PDF descargado exitosamente!');
    } else {
      toast.error('Error al generar el PDF');
    }
  };

  const handleRecoverAudio = async () => {
    if (!submissionId) {
      toast.error('Falta guardar el formulario antes de grabar el audio.');
      return;
    }

    setIsRecoveringAudio(true);
    try {
      const result = await createAudioToken(submissionId, sessionId);
      onAudioTokenChange?.(result.token);
      onAudioUrlChange?.(result.audio_url);
      window.open(result.audio_url, '_blank', 'noopener,noreferrer');
      toast.success('Abrimos el link para grabar o subir el audio.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudo preparar el link de audio.');
    } finally {
      setIsRecoveringAudio(false);
    }
  };

  const getChartData = () => {
    const labels = wheelCategories.map((c) => c.name);
    const values = wheelCategories.map((c) => wheelData[c.id]?.score || 0);
    return { labels, values };
  };

  // Get question text by ID
  const getQuestionText = (questionId) => {
    for (const section of Object.values(coachingQuestions)) {
      const question = section.questions.find(q => q.id === questionId);
      if (question) return question.text;
    }
    return questionId;
  };

  const { labels, values } = getChartData();
  const handleCreateRmPassword = async (password) => {
    if (!submissionId) throw new Error('No encontramos el formulario guardado.');
    return configureSubmissionRmPassword(submissionId, sessionId, password);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
          <CardTitle className="text-2xl">¡Felicitaciones, {userData.name}!</CardTitle>
          <CardDescription className="text-base space-y-2">
            <p>Has completado tu planificador de vida.</p>
            <p><strong>Tus respuestas han sido guardadas.</strong></p>
            {portalJourney && (
              <p className="text-sm">
                Volvé al Portal para preparar tus frases y grabar tu voz.
              </p>
            )}
            <p className="text-sm">Podés descargar un PDF con todo tu resumen.</p>
            {submissionId ? (
              <p className="text-sm text-muted-foreground italic mt-3 font-medium">
                (Esto ya quedó en nuestra base de datos, no hace falta que nos lo envíes)
              </p>
            ) : (
              <p className="text-sm text-amber-600 italic mt-3 font-medium">
                Falta confirmar el guardado en servidor. Volvé al cierre y finalizá el formulario.
              </p>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <Button
            onClick={handleExportAllPDF}
            size="lg"
            className="px-8"
            variant="outline"
            data-testid="download-all-pdf-button"
            disabled={!submissionId}
          >
            <Download className="w-5 h-5 mr-2" />
            Descargar Mi Resumen en PDF
          </Button>
          {portalJourney && submissionId && (
            <Button asChild size="lg" className="mt-4 w-full sm:w-auto sm:ml-3">
              <a href={portalUrl} rel="noreferrer">
                Continuar en el Portal
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </Button>
          )}
          {canRecoverAudio && (
            <div className="mt-4 rounded-md border border-cyan-200 bg-cyan-50/70 p-4 text-sm text-cyan-900 dark:bg-cyan-950/20 dark:border-cyan-900 dark:text-cyan-100">
              <p className="mb-3">
                Tu formulario ya quedó guardado. Si todavía no subiste el audio, podés grabarlo o cargarlo desde este link.
              </p>
              <Button
                type="button"
                onClick={handleRecoverAudio}
                disabled={isRecoveringAudio}
              >
                {isRecoveringAudio ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mic className="w-4 h-4 mr-2" />
                )}
                Grabar o subir audio
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isFormu2 && hasUploadedAudio && (
        <div
          ref={passwordSetupRef}
          tabIndex={-1}
          className="scroll-mt-48 outline-none"
          aria-label="Paso primordial: crear contraseña para la app"
        >
          <RmPasswordSetup
            email={userData.email}
            configured={rmcPasswordConfigured}
            onSubmit={handleCreateRmPassword}
            onConfigured={onRmcPasswordConfigured}
          />
        </div>
      )}

      {/* Full Summary Content for PDF */}
      <div id="full-summary-content" className="space-y-6 bg-card p-6 rounded-lg shadow-sm border">
        <div className="text-center border-b pb-4" data-pdf-section>
          <h1 className="text-2xl font-bold text-primary">Academia Cortex</h1>
          <h2 className="text-xl font-semibold mt-2">Planificador de Vida</h2>
          <p className="text-muted-foreground mt-1">{userData.name} - {userData.email}</p>
          <p className="text-sm text-muted-foreground">Generado el {new Date().toLocaleDateString('es-AR')}</p>
        </div>

        <Separator />

        {/* Life Squares Summary */}
        {lifeSquares.currentAge && lifeSquares.targetAge && (
          <div data-pdf-section className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Cuadraditos de Vida</h3>
              <LifeSquares
                currentAge={lifeSquares.currentAge}
                targetAge={lifeSquares.targetAge}
                name={userData.name}
              />
            </div>
            <Separator />
          </div>
        )}

        {/* Wheel Summary */}
        <div data-pdf-section className="pdf-keep-together">
          <h3 className="text-lg font-semibold mb-4">Rueda de la Vida</h3>
          <div className="grid lg:grid-cols-2 gap-4">
            <WheelChart labels={labels} values={values} />
            <div className="space-y-2">
              {wheelCategories.map((cat) => {
                const score = wheelData[cat.id]?.score || 0;
                return (
                  <div
                    key={cat.id}
                    className="flex items-center justify-between p-2 rounded"
                    style={{ backgroundColor: `${cat.color}20` }}
                  >
                    <span className="font-medium">{cat.name}</span>
                    <span className="font-bold text-primary">{score.toFixed(1)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <Separator />

        {/* Objectives Summary with full details */}
        <div data-pdf-section>
          <h3 className="text-lg font-semibold mb-4">Objetivos</h3>
          <div className="space-y-6">
            {['3m', '6m', '9m'].map((period) => {
              const periodObjectives = objectives[period] || [];
              const title =
                period === '3m'
                  ? 'Objetivos a 3 Meses'
                  : period === '6m'
                  ? 'Objetivos a 6 Meses'
                  : 'Objetivos a 9 Meses';
              
              if (periodObjectives.length === 0 || !periodObjectives.some(obj => obj.description)) {
                return null;
              }

              return (
                <Card key={period} className="overflow-hidden">
                  <CardHeader className="py-3 bg-muted/50">
                    <CardTitle className="text-base">{title}</CardTitle>
                  </CardHeader>
                  <CardContent className="py-4">
                    {periodObjectives.map((obj, idx) => {
                      if (!obj.description) return null;
                      return (
                        <div key={idx} className="mb-4 last:mb-0">
                          <div className="flex items-start justify-between mb-2">
                            <strong className="text-primary">{idx + 1}. {obj.description}</strong>
                            {obj.dailyTime && (
                              <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
                                {obj.dailyTime}/día
                              </span>
                            )}
                          </div>
                          {obj.actions && obj.actions.filter(a => a).length > 0 && (
                            <div className="ml-4">
                              <p className="text-sm font-medium text-muted-foreground mb-1">Acciones:</p>
                              <ul className="list-disc list-inside text-sm space-y-1">
                                {obj.actions.filter(a => a).map((action, actionIdx) => (
                                  <li key={actionIdx}>{action}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <Separator />

        {/* Question Answers Summary */}
        {Object.keys(questionAnswers).length > 0 && (
          <div data-pdf-section>
            <h3 className="text-lg font-semibold mb-4">Respuestas a las Preguntas</h3>
            <div className="space-y-3">
              {Object.entries(questionAnswers).map(([questionId, answer]) => {
                if (!answer || (Array.isArray(answer) && answer.length === 0)) return null;
                const questionText = getQuestionText(questionId);
                const displayAnswer = Array.isArray(answer) ? answer.join(', ') : answer;
                return (
                  <div key={questionId} className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-medium text-sm">{questionText}</p>
                    <p className="text-sm mt-1 text-muted-foreground">{displayAnswer}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center pt-4">
        {onBack ? (
          <Button variant="outline" onClick={onBack} data-testid="wizard-back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Editar
          </Button>
        ) : (
          <div />
        )}
        <p className="text-sm text-muted-foreground">
          Tus respuestas han sido guardadas automáticamente.
        </p>
      </div>
    </div>
  );
}

export default StepSummary;
